import { useCallback, useMemo, useState } from 'react'
import { AlertTriangle, Check, CreditCard, Lock, Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { StatusBadge } from '@/admin/components/shared/status-badge'
import { formatCurrency } from '@/admin/lib/formatting'
import { useFormatters } from '@/shared/lib/formatting'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { DatePicker } from '@/shared/components/ui/date-picker'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { Textarea } from '@/shared/components/ui/textarea'
import { DataTable } from '@/admin/components/data-table'
import { DetailCard } from '@/admin/components/shared/detail-card'
import type { DataTableColumn } from '@/admin/components/data-table'
import useForm from '@/shared/lib/forms/useForm'
import type { FormField } from '@/shared/lib/forms'
import {
  useCreatePayment,
  useProcessPayment,
  useRecordPaymentReference,
  useCancelPayment,
  useSubmitPayment,
  useVoidPayment,
} from '@/admin/hooks/use-settlement'
import type { ClaimPayment } from '@/admin/hooks/use-settlement'
import { useSettlementOptions } from '@/admin/hooks/use-settlement'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import { extractApiErrorMessage, toastApiError } from '@/shared/lib/api-error'
import { useEntityTypeByKey } from '@/admin/hooks/use-entities'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useEntityAttributeRequired,
  useEntityLookupRecord,
} from '@/admin/hooks/use-entity-registration'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { humanise } from '@/admin/pages/claims/claim-detail-helpers'
import {
  CLAIM_PAYEE_PROVIDER,
  defaultPayeeValue,
  useClaimPayeeProviders,
} from '@/admin/hooks/use-claim-payee-lookup'
import type { PayeeCandidate } from '@/admin/hooks/use-claim-payee-lookup'

export type { PayeeCandidate }

interface Props {
  claimUuid: string
  payments: ClaimPayment[]
  /** Parties this claim can pay. Empty disables the picker's suggestions. */
  payeeCandidates?: PayeeCandidate[]
  isEditable: boolean
  // Driven by the workflow state's ``allow_payment_processing`` EAV
  // attribute. The per-row "Process Payment" button only shows when the
  // current state has opted in (typically the settlement-audit state).
  allowPaymentProcessing: boolean
  currency?: string
  /** A home under a claim that pays as one: the claim number that pays it. */
  paidThrough?: string | null
}

/**
 * The server's duplicate-reference warning, or null.
 *
 * Keyed on ``duplicate_reference`` rather than matched against the copy, so
 * rewording the message cannot quietly turn the confirmation step off.
 */
function extractDuplicateWarning(err: unknown): string | null {
  const result = (err as { response?: { data?: { result?: unknown } } })
    ?.response?.data?.result
  if (!result || typeof result !== 'object') return null
  const entry = (result as Record<string, unknown>).duplicate_reference
  if (Array.isArray(entry) && typeof entry[0] === 'string') return entry[0]
  return typeof entry === 'string' ? entry : null
}

type PaymentFormValues = {
  payment_type: string
  amount: string
  note: string
}

export function SettlementPaymentsTable({
  claimUuid,
  payments,
  isEditable,
  allowPaymentProcessing,
  currency = 'USD',
  payeeCandidates = [],
  paidThrough = null,
}: Props) {
  const { t } = useTranslation()
  const { locale, formatDateNumeric } = useFormatters()
  const { can } = usePermissions()
  // WPS-175 #11: recording a payment reference asserts that money moved, so
  // it is restricted to settlement specialists and administrators.
  // ``claims.pay_claim`` is already exactly that set - Claim Leader is
  // deliberately excluded from it under the segregation-of-duties rule - so
  // it is the existing capability for this, not a new one. Enforced
  // server-side too; this only keeps the button from lying about what the
  // user can do.
  const canRecordPayment = can('claims.pay_claim')
  // WPS-243: voiding a paid payment is its own capability.
  const canVoidPayment = can('claims.void_payment')
  const [voidTarget, setVoidTarget] = useState<ClaimPayment | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [processTarget, setProcessTarget] = useState<ClaimPayment | null>(null)
  // A routed payment has nothing to ask for - the provider mints the reference -
  // so it confirms instead of opening the capture dialog.
  const [submitTarget, setSubmitTarget] = useState<ClaimPayment | null>(null)
  const [processReference, setProcessReference] = useState('')
  const [processPostDate, setProcessPostDate] = useState('')
  const [processPostDateError, setProcessPostDateError] = useState(false)
  const [processNote, setProcessNote] = useState('')
  const [processReferenceError, setProcessReferenceError] = useState(false)
  // The server's duplicate-reference warning, held so the operator can
  // read what already exists and confirm rather than being blocked.
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null)

  // WPS-175 #15: process every pending payment in one click.
  //
  // Penny's complaint was that the ONLY option was all-at-once, which does
  // not match a workflow where a check number can arrive a day after a
  // credit's. The answer is both options, not the opposite default: one at a
  // time from the row, or the whole lot from here when every reference is
  // already to hand.
  //
  // Deliberately implemented as N calls to the SAME per-payment endpoint the
  // row button uses, rather than by restoring the old `process_pending_payments`
  // side-effect. That handler took no reference argument, so the number the
  // user typed was silently dropped; going through the per-payment path means
  // every payment records a real reference and the claim still advances by
  // itself when the last one settles.
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkReference, setBulkReference] = useState('')
  const [bulkPostDate, setBulkPostDate] = useState('')
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkError, setBulkError] = useState<string | null>(null)

  // WPS-175 #15: a transient toast is the wrong place to confirm that money
  // moved. Processing a payment is the end of a task - the operator needs to
  // see that it landed, what reference it landed under, and whether the claim
  // finished - not be returned to the page with a message that has already
  // faded. Cleared by the operator, not by a timer.
  const [processedSummary, setProcessedSummary] = useState<{
    label: string
    amount: string
    reference: string
    remaining: number
    advancedTo: string | null
  } | null>(null)
  // WPS-175 #11: a reference already synchronised from SAP or the automated
  // check download is final; manual entry is only for the blank case.
  const referenceLocked = processTarget?.reference_is_locked ?? false
  // WPS-165 #13/#14: recording the reference for a payment already made.
  const [referenceTarget, setReferenceTarget] = useState<ClaimPayment | null>(
    null,
  )
  // The duplicate warning and the number it was about: confirming covers
  // that number only.
  const [referenceDuplicate, setReferenceDuplicate] = useState<{
    value: string
    message: string
  } | null>(null)
  const [cancelTarget, setCancelTarget] = useState<ClaimPayment | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelReasonError, setCancelReasonError] = useState('')

  const createMut = useCreatePayment(claimUuid)
  const processMut = useProcessPayment(claimUuid)
  const referenceMut = useRecordPaymentReference(claimUuid)
  const submitMut = useSubmitPayment(claimUuid)
  const cancelMut = useCancelPayment(claimUuid)
  const { data: settlementOptions } = useSettlementOptions(claimUuid)
  const paymentTypeOptions = useMemo(
    () => settlementOptions?.payment_types ?? [],
    [settlementOptions],
  )
  // A tenant whose payments come from a provider has no reference to hold.
  const referenceRequired = useEntityAttributeRequired(
    'claim_payment',
    'reference_number',
  )
  // The post date goes with a reference only when the tenant requires it.
  const postDateRequired = useEntityAttributeRequired(
    'claim_payment',
    'payment_date',
  )

  // WPS-165 #13/#14: the reference for a payment already made.
  const referenceFields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'reference_number',
        label: t('settlement.payments.referenceNumberLabel', 'Reference Number'),
        value: '',
        required: t(
          'settlement.payments.referenceRequiredToSave',
          'Enter the reference number.',
        ),
        placeholder: t(
          'settlement.payments.referencePlaceholder',
          'Check #, ACH confirmation, credit # …',
        ),
        // An identifier: keep the case as typed.
        autoCapitalize: false,
        // A different number is a different question - retract the warning.
        onChange: (next) =>
          setReferenceDuplicate((prev) =>
            prev && prev.value !== String(next ?? '').trim() ? null : prev,
          ),
      },
      {
        type: 'datepicker',
        name: 'payment_date',
        label: t('settlement.payments.postDateLabel', 'Post Date'),
        value: '',
        ...(postDateRequired
          ? {
              required: t(
                'settlement.payments.postDateRequiredWithReference',
                'Enter the post date for this reference number.',
              ),
            }
          : {}),
      },
    ],
    [postDateRequired, t],
  )

  const { Form: ReferenceForm, ref: referenceFormRef } = useForm(
    {
      name: 'settlement-payment-reference',
      submit: 'footer',
      fields: referenceFields,
    },
    async (rawValues, ctx) => {
      if (!referenceTarget) return
      const value = String(rawValues.reference_number ?? '').trim()
      // `required` lets whitespace through; a blank number is still missing.
      if (!value) {
        ctx.setFieldError(
          'reference_number',
          t(
            'settlement.payments.referenceRequiredToSave',
            'Enter the reference number.',
          ),
        )
        return
      }
      try {
        await referenceMut.mutateAsync({
          paymentUuid: referenceTarget.uuid,
          reference_number: value,
          payment_date: String(rawValues.payment_date ?? '') || null,
          // Only once the operator has confirmed this exact number.
          acknowledge_duplicate:
            referenceDuplicate?.value === value || undefined,
        })
        setReferenceTarget(null)
        setReferenceDuplicate(null)
        ctx.resetForm()
      } catch (err) {
        // The server asks rather than forbids (one cheque for two payments is
        // real), so the warning stays in the dialog as a question.
        const duplicate = extractDuplicateWarning(err)
        if (duplicate) {
          setReferenceDuplicate({ value, message: duplicate })
          return
        }
        // Field errors come back under ``result``, not ``errors``.
        const result = (
          err as { response?: { data?: { result?: Record<string, unknown> } } }
        )?.response?.data?.result
        const firstError = (key: string) => {
          const entry = result?.[key]
          return Array.isArray(entry) && typeof entry[0] === 'string'
            ? entry[0]
            : undefined
        }
        const dateError = firstError('payment_date')
        if (dateError) {
          ctx.setFieldError('payment_date', dateError)
          return
        }
        ctx.setFieldError(
          'reference_number',
          firstError('reference_number') ??
            t(
              'settlement.payments.referenceSaveFailed',
              'That reference could not be saved. Try again.',
            ),
        )
      }
    },
  )

  // Reset on open and on close, so every payment starts from a clean form.
  const openReferenceDialog = useCallback(
    (payment: ClaimPayment) => {
      referenceFormRef.current?.resetForm()
      setReferenceDuplicate(null)
      setReferenceTarget(payment)
    },
    [referenceFormRef],
  )
  const closeReferenceDialog = useCallback(() => {
    setReferenceTarget(null)
    setReferenceDuplicate(null)
    referenceFormRef.current?.resetForm()
  }, [referenceFormRef])

  const recipientTypeOptions = useMemo(
    () => settlementOptions?.recipient_types ?? [],
    [settlementOptions],
  )

  // Split rows by status - the pending group is PENDING (created by sync from line
  // items, awaiting AP / SAP processing), Processed = PROCESSED. Status
  // is the single source of truth, no derived grouping in the UI.
  const pendingPayments = useMemo(
    () => payments.filter((p) => p.status === 'PENDING'),
    [payments],
  )
  // What Process All may take: a held payment waits for its own release.
  const processablePayments = useMemo(
    () => pendingPayments.filter((p) => !p.blocked_message),
    [pendingPayments],
  )
  // Processed means PROCESSED, and nothing else. This group used to be
  // "everything that is not PENDING", so a payment merely submitted to the
  // bank - and a cancelled one - sat under a heading that said PROCESSED,
  // contradicting its own status badge. That is the same untruth requirement
  // 10 exists to remove, so anything in between gets its own group.
  const processedPayments = useMemo(
    () => payments.filter((p) => p.status === 'PROCESSED'),
    [payments],
  )
  const inFlightPayments = useMemo(
    () =>
      payments.filter(
        (p) =>
          p.status !== 'PENDING' &&
          p.status !== 'PROCESSED' &&
          p.status !== 'CANCELLED',
      ),
    [payments],
  )
  // Cancelled is its own group for the same reason PROCESSED is: it was
  // falling into the in-flight bucket and rendering under a heading that said
  // "Submitted", which is the untruth the split above exists to remove. It
  // only became visible once anything could produce a cancelled row.
  const cancelledPayments = useMemo(
    () => payments.filter((p) => p.status === 'CANCELLED' && !p.is_voided),
    [payments],
  )
  // A voided payment was paid first, so a Cancelled heading would contradict
  // its own badge.
  const voidedPayments = useMemo(
    () => payments.filter((p) => p.status === 'CANCELLED' && p.is_voided),
    [payments],
  )

  // The tenant's `claim_payment` registration decides which of its own
  // attributes reach this table. Anything already rendered by a built-in
  // column above is excluded so a declaration cannot duplicate one.
  const { data: paymentEntity } = useEntityTypeByKey('claim_payment')
  const registryColumns = useMemo<DataTableColumn<ClaimPayment>[]>(() => {
    const builtIn = new Set([
      'recipient_type',
      'payee',
      'payment_type',
      'amount',
      'reference_number',
      'settlement_reference',
      'external_reference',
      'payment_date',
      'post_date',
      'status',
      'note',
    ])
    return (paymentEntity?.attributes ?? [])
      .filter(
        (attribute) =>
          attribute.configuration_data?.display?.table === true &&
          !builtIn.has(attribute.code_name),
      )
      .map((attribute) => ({
        id: attribute.code_name,
        header:
          attribute.configuration_data?.display?.label || attribute.display_name,
        cell: (row: ClaimPayment) => {
          const value = (row as unknown as Record<string, unknown>)[
            attribute.code_name
          ]
          if (value === null || value === undefined || value === '') {
            return <span className="text-[var(--ink-3)]">-</span>
          }
          return (
            <span className="tabular-nums">
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </span>
          )
        },
      }))
  }, [paymentEntity])

  // Recipient / payee are EAV: a tenant that has not declared them would
  // otherwise get two permanently blank columns (and no way to fill them).
  // Gated on the tenant's own ledger attributes rather than on a tenant name.
  // While the lookup is still resolving we keep showing them, so a tenant that
  // does have them never sees the columns flicker in.
  const ledger = useEntityLookupRecord('claim_payment')
  const declares = useCallback(
    (code: string) => ledger === null || ledger.attribute_codes.includes(code),
    [ledger],
  )

  const lookupProviders = useClaimPayeeProviders(payeeCandidates)

  // WPS-243: a voided payment and its replacement name each other by
  // reference, and the voided one says when, by whom and why.
  const paymentTrail = useCallback(
    (row: ClaimPayment): string[] => {
      const trail: string[] = []
      if (row.replaces_reference)
        trail.push(
          t('settlement.payments.replaces', {
            defaultValue: 'Replaces {{reference}}',
            reference: row.replaces_reference,
          }),
        )
      if (!row.is_voided) return trail
      const date = row.cancelled_at ? formatDateNumeric(row.cancelled_at) : ''
      if (date)
        trail.push(
          row.cancelled_by_name
            ? t('settlement.payments.voidedOnBy', {
                defaultValue: 'Voided on {{date}} by {{name}}',
                date,
                name: row.cancelled_by_name,
              })
            : t('settlement.payments.voidedOn', {
                defaultValue: 'Voided on {{date}}',
                date,
              }),
        )
      if (row.cancellation_reason) trail.push(row.cancellation_reason)
      if (row.replaced_by)
        trail.push(
          row.replaced_by.reference
            ? t('settlement.payments.replacedBy', {
                defaultValue: 'Replaced by {{reference}}',
                reference: row.replaced_by.reference,
              })
            : t('settlement.payments.replacedByUnpaid', {
                defaultValue: 'Replaced by {{amount}} ({{status}})',
                amount: formatCurrency(
                  Number(row.replaced_by.amount),
                  currency,
                  locale,
                ),
                status: row.replaced_by.status_label,
              }),
        )
      return trail
    },
    [t, formatDateNumeric, currency, locale],
  )

  const referenceValue = useCallback(
    (row: ClaimPayment) => {
      // WPS-165 #14: a reference that exists is locked, and the lock says
      // why. Disabling a control without an explanation reads as broken;
      // "Synchronised from the payment system" reads as correct. The
      // sentence comes from the API so the model's own vocabulary and the
      // screen cannot drift apart.
      if (row.settlement_reference) {
        return (
          <span className="inline-flex flex-col gap-0.5">
            <span className="inline-flex items-center gap-1.5">
              {row.settlement_reference}
              {/* --rose, not muted ink: a lock is the tenant's mark that
                  this value is settled, and it derives from --brand so it
                  reads as OC's pink for OC and as each other tenant's own
                  colour, never a hardcoded hex. Its OKLCH lightness is
                  pinned, so it clears AA against the row for every brand. */}
              <Lock aria-hidden className="h-3 w-3 shrink-0 text-[var(--rose)]" />
            </span>
            {/* Stated inline rather than behind a hover. Provenance is
                what an auditor and a settlement specialist both need to
                see, and a tooltip hides it from anyone reading rather
                than pointing - including on a touch screen, and including
                the readers this product is least willing to lose. */}
            {row.reference_origin && (
              <span className="text-[11px] font-light leading-[1.35] text-[var(--ink-3)]">
                {row.reference_origin}
              </span>
            )}
          </span>
        )
      }
      // A provider-routed payment carries the provider's own reference.
      if (
        row.status === 'PROCESSED' &&
        !row.disbursement_provider &&
        canRecordPayment
      ) {
        return (
          <Button
            variant="ghost"
            size="xs"
            // --rose is the token for a surface that INVITES an action
            // rather than reports a state, which is exactly what an empty
            // Reference cell is. Derived from --brand, so this reads as
            // OC's pink for OC and as the tenant's own colour elsewhere -
            // never a hardcoded hex. Its lightness is pinned in OKLCH so
            // it clears AA on 14px text for every brand (7.15:1 on OC's).
            className="h-auto px-0 text-[var(--rose)] hover:bg-transparent hover:underline hover:text-[var(--rose-hover)]"
            onClick={(e) => {
              e.stopPropagation()
              openReferenceDialog(row)
            }}
          >
            {t('settlement.payments.addReference', 'Add reference')}
          </Button>
        )
      }
      return <span className="text-[var(--ink-3)]">-</span>
    },
    [t, canRecordPayment, openReferenceDialog],
  )

  const columns = useMemo<DataTableColumn<ClaimPayment>[]>(
    () => [
      ...(declares('recipient_type')
        ? [
            {
              id: 'recipient_type',
              header: t('settlement.payments.recipientLabel', 'Recipient'),
              cell: (row: ClaimPayment) => {
                if (!row.recipient_type) {
                  return <span className="text-[var(--ink-3)]">-</span>
                }
                const label =
                  recipientTypeOptions.find(
                    (o) => o.value === row.recipient_type,
                  )?.label ?? humanise(row.recipient_type)
                return <Badge variant="outline">{label}</Badge>
              },
            },
          ]
        : []),
      ...(declares('payee')
        ? [
            {
              id: 'payee',
              header: t('settlement.payments.payeeLabel', 'Payee'),
              cell: (row: ClaimPayment) =>
                row.payee || <span className="text-[var(--ink-3)]">-</span>,
            },
          ]
        : []),
      {
        id: 'payment_type',
        header: t('settlement.payments.methodLabel', 'Method'),
        cell: (row) => {
          const label =
            paymentTypeOptions.find((o) => o.value === row.payment_type)
              ?.label ?? row.payment_type
          return <Badge variant="outline">{label}</Badge>
        },
      },
      {
        id: 'amount',
        header: t('settlement.payments.amountLabel', 'Amount'),
        cell: (row) => {
          const isNegative = row.amount < 0
          return (
            <span
              className={isNegative ? 'text-[var(--color-destructive)]' : ''}
            >
              {formatCurrency(row.amount, currency, locale)}
            </span>
          )
        },
      },
      // WPS-175 #10. The Reference column used to render
      // ``reference_number``, which is the ``PAY-...`` key we mint on
      // insert - so every unpaid payment looked like it already carried a
      // reference. It now shows the check / credit number the payment
      // actually settled under, which does not exist until it is paid.
      // WPS-165 #13/#14: when a processed payment has no reference yet, the
      // empty cell IS the way to add one. A cheque number comes back days
      // later, often to someone else, and making them re-open the Process
      // dialog to record it is a trip through an action that no longer
      // applies. The thing you are looking at is the thing you click.
      {
        id: 'settlement_reference',
        header: t('settlement.payments.referenceLabel', 'Reference'),
        cell: (row) => {
          const trail = paymentTrail(row)
          if (trail.length === 0) return referenceValue(row)
          return (
            <span className="inline-flex flex-col gap-1">
              {referenceValue(row)}
              <span className="flex max-w-[260px] flex-col gap-0.5 text-[11px] font-light leading-[1.35] text-[var(--ink-3)]">
                {trail.map((line, idx) => (
                  <span key={idx}>{line}</span>
                ))}
              </span>
            </span>
          )
        },
      },
      // Post date, blank until the payment is processed. The API returns
      // null before then, so there is nothing to suppress here.
      {
        id: 'post_date',
        header: t('settlement.payments.postDateLabel', 'Post Date'),
        cell: (row) =>
          row.post_date ? (
            <span className="tabular-nums">
              {formatDateNumeric(row.post_date)}
            </span>
          ) : (
            <span className="text-[var(--ink-3)]">-</span>
          ),
      },
      {
        id: 'status',
        header: t('settlement.payments.statusLabel', 'Status'),
        cell: (row) => (
          /* ``StatusBadge`` is the house status pill: it derives its own
             colour from the status text and humanises the label, so every
             status surface in the app agrees. The local status -> variant map
             this replaces had no entry for SUBMITTED_TO_EXTERNAL, which fell
             to the default and rendered identically to PENDING.
             ``status_label`` is the model's ``status_label`` (its display
             name, or "Voided" for a cancelled payment that had been paid), so
             a status added later can never leak its raw value, and the badge
             can never contradict the record. Do NOT special-case a status to
             different copy here - change the model's label instead. */
          <span className="inline-flex flex-col items-start gap-0.5">
            <StatusBadge status={row.status_label || row.status} />
            {row.failure_reason && (
              <span className="max-w-[240px] text-[11px] font-light leading-[1.35] text-[var(--status-error)]">
                {row.failure_reason}
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'note',
        header: t('settlement.payments.noteLabel', 'Note'),
        accessorKey: 'note',
      },
      // Columns the tenant asked for, not columns we chose. Any attribute
      // they declare on `claim_payment` with `display.table` appears here -
      // so a tenant recording a clearing number gets one and a tenant that
      // does not never sees an empty column.
      ...registryColumns,
      // Per-payment actions are a status transition, not a modification.
      // Gated by the workflow state's ``allow_payment_processing`` EAV
      // attribute - only states that opt in (typically settlement-audit)
      // expose it. The SAP public API path is not gated by this flag.
      //
      // WPS-175 #15: labelled "Process Payment", not "Mark Processed".
      // Penny's AC names a "Process Payment button", and after the header
      // action was renamed to "Authorise Payments" the only thing that
      // actually moved money was called something she was not looking for -
      // she asked "where is the process payment button?". The dialog is
      // already titled Process Payment; the button now agrees with it.
      {
        id: 'actions',
        header: '',
        cell: (row: ClaimPayment) => (
          <div className="flex items-center gap-1">
            {/* WPS-165 #14: offered on an ISSUED payment too, not only a
                pending one. An issued payment is one already handed to the
                bank and waiting for its cheque number to come back - which
                is exactly the case the requirement is about ("when the value
                has not been automatically synchronized into the system from
                SAP"). Gating on PENDING alone left the only manual-entry
                route closed at the very moment it was needed: the operator
                could cancel the payment, but could not record the reference
                for it. The API already accepts any payment that is not yet
                processed, so only this button was missing. */}
            {/* A held payment (e.g. waiting on its voucher) says why instead. */}
            {row.blocked_message ? (
              <span className="text-[11px] text-[var(--ink-3)]">
                {row.blocked_message}
              </span>
            ) : null}
            {(row.status === 'PENDING' ||
              row.status === 'SUBMITTED_TO_EXTERNAL') &&
              // WPS-244: a payment its voucher released is payable at any stage.
              (allowPaymentProcessing || row.released) &&
              canRecordPayment &&
              !row.blocked_message && (
              <Button
                variant="outline"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation()
                  if (row.disbursement_provider) setSubmitTarget(row)
                  else {
                    setProcessTarget(row)
                    // A number typed on the row before it was paid.
                    setProcessReference(row.held_reference ?? '')
                  }
                }}
              >
                {t('claims.payments.processPayment', 'Process Payment')}
              </Button>
            )}
            {/* Not offered on a processed payment. The API can still cancel
                one (the paying system knows whether the cheque cleared, and
                has to be able to correct us), but it costs an explicit
                acknowledgement. A button of equal weight next to a settled
                payment invites the opposite reading - that clicking it undoes
                the payment, which it does not. */}
            {(row.status === 'PENDING' ||
              row.status === 'SUBMITTED_TO_EXTERNAL') &&
              allowPaymentProcessing &&
              canRecordPayment && (
              <Button
                variant="outline"
                size="xs"
                className="text-[var(--status-error)]"
                onClick={(e) => {
                  e.stopPropagation()
                  setCancelTarget(row)
                  setCancelReason('')
                  setCancelReasonError('')
                }}
              >
                {t('settlement.payments.cancelPayment', 'Cancel')}
              </Button>
            )}
            {/* WPS-243: a paid payment is voided, not cancelled, so it can be
                corrected and reissued. The server says which can be. */}
            {row.voidable && canVoidPayment && (
              <Button
                variant="outline"
                size="xs"
                className="text-[var(--status-error)]"
                onClick={(e) => {
                  e.stopPropagation()
                  setVoidTarget(row)
                }}
              >
                {t('settlement.payments.void', 'Void')}
              </Button>
            )}
          </div>
        ),
      } satisfies DataTableColumn<ClaimPayment>,
    ],
    [
      // `t` so the column re-renders on a language change; the row button's
      // label comes from it.
      t,
      allowPaymentProcessing,
      canRecordPayment,
      canVoidPayment,
      paymentTrail,
      referenceValue,
      currency,
      locale,
      formatDateNumeric,
      paymentTypeOptions,
      recipientTypeOptions,
      registryColumns,
      declares,
    ],
  )

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false)
  }, [])

  const hasExistingPayments = payments.length > 0

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'select',
        name: 'payment_type',
        label: 'Payment Type',
        // No default. This used to preselect 'CR' - one tenant's code for a
        // credit - which every other tenant's ledger has never heard of. The
        // form then held a value the select could not display, so `required`
        // passed silently and the payment was written with a type outside the
        // tenant's own vocabulary.
        value: '',
        required: 'Payment type is required',
        options: paymentTypeOptions.map((o) => ({
          value: o.value,
          label: o.label,
        })),
        className: 'col-span-2',
      },
      {
        type: 'number',
        name: 'amount',
        label: 'Amount',
        value: '',
        required: 'Amount is required',
        className: 'col-span-2',
      },
      // No reference here: a payment takes one when it is processed, or after.
      // Who gets the money. Pre-filled only when the claim has exactly one
      // candidate - with several, a default would be accepted by reflex and
      // the wrong party paid. Always rendered, never conditional on there
      // being more than one: on a single-party claim paying someone else (the
      // repair shop that did the work) is the likeliest case of all, and
      // hiding the control would make it impossible.
      ...(declares('payee')
        ? [
            {
              type: 'entity-search' as const,
              name: 'payee',
              label: t('settlement.payments.payeeLabel', 'Payee'),
              value: defaultPayeeValue(payeeCandidates),
              configurationData: {
                lookup_provider: CLAIM_PAYEE_PROVIDER,
                allow_create: true,
                icon: 'user',
              },
              className: 'col-span-2',
            },
          ]
        : []),
      {
        type: 'textarea',
        name: 'note',
        label: 'Note',
        value: '',
        ...(hasExistingPayments
          ? { required: 'Note is required for additional payments' }
          : {}),
        className: 'col-span-2',
      },
    ],
    [hasExistingPayments, paymentTypeOptions, declares, payeeCandidates, t],
  )

  const { Form, ref } = useForm(
    {
      name: 'settlement-payment-create',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as PaymentFormValues
      // The picker yields {uuid, name}; the ledger stores a name plus the kind
      // of party it was. Resolve the kind from the candidate that was chosen -
      // a free-text entry matches none of them and is simply "other".
      const picked = rawValues.payee as
        { uuid?: string; name?: string } | string | undefined
      const payeeName =
        typeof picked === 'string'
          ? picked.trim()
          : (picked?.name ?? '').trim()
      const matched = payeeCandidates.find(
        (c) =>
          c.uuid && c.uuid === (typeof picked === 'string' ? '' : picked?.uuid),
      )
      // Only record a kind the tenant's own vocabulary contains. A tenant that
      // calls its customers something else (Owens Corning says HOMEOWNER) must
      // not have 'CUSTOMER' written into a field that feeds their payment
      // grouping - the name is always right, the classification only when it
      // maps. Nothing to map against yet (options still loading) writes it.
      const derived = matched?.recipient_type ?? 'OTHER'
      const recipientType =
        recipientTypeOptions.length === 0 ||
        recipientTypeOptions.some((o) => o.value === derived)
          ? derived
          : ''
      const payload = {
        payment_type: values.payment_type,
        amount: values.amount,
        note: (values.note ?? '').trim(),
        ...(payeeName
          ? {
              payee: payeeName,
              ...(recipientType ? { recipient_type: recipientType } : {}),
            }
          : {}),
      }
      try {
        await createMut.mutateAsync(payload)
        toast.success('Payment added')
        handleDialogClose()
      } catch (err) {
        toastApiError(err, 'Failed to add payment')
      }
    },
    [dialogOpen, fields],
  )

  const closeProcessDialog = useCallback(() => {
    setProcessTarget(null)
    setProcessReference('')
    setProcessPostDate('')
    setProcessPostDateError(false)
    setProcessNote('')
    setProcessReferenceError(false)
    setDuplicateWarning(null)
  }, [])

  // Process every pending payment, sequentially, through the per-payment
  // endpoint. Sequential rather than parallel on purpose: the duplicate
  // reference check is a read-then-write on the server, so firing N of them
  // at once would race and let two payments claim the same number. The
  // shared reference is acknowledged as a duplicate from the second payment
  // on, which is the honest thing to do - the operator asked for one
  // reference across the batch.
  const handleBulkProcess = useCallback(async () => {
    const ref = bulkReference.trim()
    if (!ref) {
      setBulkError(
        t(
          'claims.payments.enterReferenceError',
          'Enter a reference to apply to every payment.',
        ),
      )
      return
    }
    if (!bulkPostDate && postDateRequired) {
      setBulkError(
        t(
          'settlement.payments.postDateRequiredWithReference',
          'Enter the post date for this reference number.',
        ),
      )
      return
    }
    setBulkBusy(true)
    setBulkError(null)
    let done = 0
    try {
      for (const payment of processablePayments) {
        await processMut.mutateAsync({
          paymentUuid: payment.uuid,
          reference_number: payment.reference_is_locked
            ? payment.settlement_reference
            : ref,
          payment_date: bulkPostDate || null,
          // The batch shares one reference by the operator's own choice, so
          // every payment after the first would otherwise stop on the
          // duplicate warning they have already answered.
          acknowledge_duplicate: done > 0 || undefined,
        })
        done += 1
      }
      toast.success(
        done === 1
          ? t('claims.payments.processedToast', 'Payment processed')
          : t('claims.payments.paymentsProcessedCount', {
              defaultValue: '{{count}} payments processed',
              count: done,
            }),
      )
      setBulkOpen(false)
      setBulkReference('')
      setBulkPostDate('')
    } catch (err) {
      // Whatever succeeded before the failure stays processed - the money
      // moved. Say how far it got rather than implying the batch rolled back.
      setBulkError(
        done > 0
          ? `Processed ${done} of ${processablePayments.length}, then stopped: ${extractApiErrorMessage(err) ?? 'the payment was rejected'}`
          : (extractApiErrorMessage(err) ?? 'Failed to process payment'),
      )
    } finally {
      setBulkBusy(false)
    }
  }, [
    bulkReference,
    bulkPostDate,
    postDateRequired,
    processablePayments,
    processMut,
    t,
  ])

  const handleProcess = useCallback(() => {
    if (!processTarget) return
    // A locked payment already carries its reference; send that back rather
    // than an empty string, so the server sees the value it already holds.
    const ref = referenceLocked
      ? processTarget.settlement_reference
      : processReference.trim()
    if (!ref && referenceRequired) {
      setProcessReferenceError(true)
      return
    }
    // WPS-165 #13: a reference and its post date are captured together,
    // where the tenant requires the post date.
    if (ref && !processPostDate && postDateRequired) {
      setProcessPostDateError(true)
      return
    }
    processMut.mutate(
      {
        paymentUuid: processTarget.uuid,
        reference_number: ref,
        payment_date: processPostDate || null,
        note: processNote.trim() || null,
        // Set only once the operator has read the warning and confirmed.
        acknowledge_duplicate: duplicateWarning !== null || undefined,
      },
      {
        onSuccess: (result) => {
          const advancedTo =
            (result as { claim_advanced_to?: string | null } | undefined)
              ?.claim_advanced_to ?? null
          const covers = processTarget?.covers ?? []
          setProcessedSummary({
            label:
              covers
                .map((item) => item.label)
                .filter(Boolean)
                .join(', ') ||
              t('claims.payments.paymentTotal', 'Payment total'),
            amount: formatCurrency(processTarget.amount, currency, locale),
            reference: ref,
            // What is left to do, so the operator knows whether they are done.
            remaining: Math.max(processablePayments.length - 1, 0),
            advancedTo,
          })
          toast.success(
            t('claims.payments.processedToast', 'Payment processed'),
          )
          closeProcessDialog()
        },
        onError: (err) => {
          const duplicate = extractDuplicateWarning(err)
          if (duplicate) {
            // Shown in the dialog, not as a toast: it asks a question, and a
            // toast that disappears is the wrong place for one.
            setDuplicateWarning(duplicate)
            return
          }
          toastApiError(err, 'Failed to process payment')
        },
      },
    )
  }, [
    // The success panel is built here, so it reads the formatter's locale and
    // currency and the count of what is left. Omitting them would freeze all
    // three at first render and confirm the wrong thing.
    currency,
    locale,
    processablePayments.length,
    t,
    processTarget,
    processReference,
    processPostDate,
    // Read inside the callback to decide whether to send the
    // acknowledgement. Omitting it captured a stale null, so "Process
    // anyway" sent no acknowledgement and the warning returned forever.
    duplicateWarning,
    processNote,
    processMut,
    referenceLocked,
    referenceRequired,
    postDateRequired,
    closeProcessDialog,
  ])

  // The confirm stays enabled on an empty reason and validates on click, so
  // the missing field is named rather than the button silently doing nothing.
  const handleCancelPayment = useCallback(() => {
    if (!cancelTarget) return
    const reason = cancelReason.trim()
    if (!reason) {
      setCancelReasonError(
        t('settlement.payments.cancelReasonRequired', 'A reason is required.'),
      )
      return
    }
    setCancelReasonError('')
    cancelMut.mutate(
      {
        paymentUuid: cancelTarget.uuid,
        data: { reason },
      },
      {
        onSuccess: () => {
          toast.success(
            t('settlement.payments.cancelSuccess', 'Payment cancelled'),
          )
          setCancelTarget(null)
          setCancelReason('')
        },
        onError: (err) =>
          toastApiError(
            err,
            t('settlement.payments.cancelFailed', 'Failed to cancel payment'),
          ),
      },
    )
  }, [cancelTarget, cancelReason, cancelMut, t])

  const busy = createMut.isPending
  const formatOptions = { currency }

  const headerActions = (
    <div className="flex items-center gap-2">
      {/* WPS-175 #15: the batch option, alongside the per-row one.
        Only when there is more than one payment to batch - with a
        single pending payment the row button is the whole job and a
        second control asking the same question is noise. */}
      {allowPaymentProcessing &&
        canRecordPayment &&
        processablePayments.length > 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setBulkError(null)
              setBulkOpen(true)
            }}
          >
            {t('claims.payments.processAll', 'Process All')} (
            {processablePayments.length})
          </Button>
        )}
      {isEditable && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
        >
          <Plus size={14} className="mr-1" />
          {t('claims.payments.addPayment', 'Add Payment')}
        </Button>
      )}
    </div>
  )

  return (
    <DetailCard
      title={t('claims.payments.title', 'Payments')}
      icon={<CreditCard className="h-3.5 w-3.5" />}
      collapsible={false}
      trailing={headerActions}
    >
      <div className="flex flex-col gap-4 p-4">
        {/* WPS-175 #15: the end-of-task confirmation. A toast fades before
          anyone reads it, and the operator was left on a page that looked the
          same as before with no word that money had moved. This states what
          was paid, under which reference, what is left, and - when the claim
          finished - that it finished. Dismissed by the operator, not a timer. */}
        {processedSummary && (
          <div className="mb-4 flex items-start gap-2 rounded-[8px] border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--green)] bg-[var(--bg-light)] px-4 py-3">
            <Check
              size={15}
              className="mt-0.5 shrink-0 text-[var(--green)]"
              strokeWidth={2}
            />
            <div className="flex flex-col gap-0.5">
              <p className="text-[13px] font-medium text-[var(--ink)]">
                {processedSummary.label} {'\u00b7'} {processedSummary.amount}{' '}
                {t('claims.payments.processedHeadline', 'processed')}
              </p>
              <p className="text-[11px] text-[var(--ink-3)]">
                {/* WPS-165 #13: the reference is optional now, so the banner
                    says "no reference yet" rather than printing the label
                    with nothing after it. */}
                {processedSummary.reference
                  ? `${t('claims.payments.referenceLabel', 'Payment reference')} ${processedSummary.reference}`
                  : t(
                      'claims.payments.noReferenceYet',
                      'No reference yet - add it from the Reference column',
                    )}
                {processedSummary.advancedTo
                  ? ` \u00b7 ${t('claims.payments.claimMovedTo', 'claim moved to')} ${processedSummary.advancedTo.replace(/_/g, ' ')}`
                  : processedSummary.remaining > 0
                    ? ` \u00b7 ${processedSummary.remaining} ${t('claims.payments.stillPending', 'still to process')}`
                    : ''}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon-xs"
              className="ml-auto"
              onClick={() => setProcessedSummary(null)}
              aria-label={t('common.dismiss', 'Dismiss')}
            >
              <X size={13} />
            </Button>
          </div>
        )}

        {payments.length === 0 && paidThrough ? (
          <div className="text-[13px] font-light text-[var(--ink-3)]">
            {t('claims.payments.paidThrough', {
              claimNumber: paidThrough,
              defaultValue: 'Paid through {{claimNumber}}, in its one payment.',
            })}
          </div>
        ) : payments.length === 0 ? (
          <div className="text-[13px] font-light text-[var(--ink-3)]">
            {t('claims.payments.noneRecorded', 'No payments recorded.')}{' '}
            {isEditable &&
              'Fill in Recipient / Payee / Method / Date on a line item, or click "Add Payment" to record one directly.'}
          </div>
        ) : (
          <>
            {/* WPS-175 #15: the group is only drawn when something is
              pending. It used to render its heading and "No pending
              payments." on every claim, so a fully paid claim opened on a
              heading, a sentence saying nothing was there, and a gap - with
              the payments that DID exist pushed below the fold. An empty
              group is not information; the group that has rows is. */}
            {pendingPayments.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  {t('settlement.payments.groupPending', 'Pending')}
                </span>
                <DataTable
                  tableId="settlement-payments-planned"
                  columns={columns}
                  data={pendingPayments}
                  rowKey="uuid"
                  formatOptions={formatOptions}
                  emptyMessage=""
                />
              </div>
            )}

            {inFlightPayments.length > 0 && (
              <>
                <Separator className="opacity-60" />
                <div className="flex flex-col gap-2">
                  <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                    {t('settlement.payments.groupSubmitted', 'Issued')}
                  </span>
                  <DataTable
                    tableId="settlement-payments-in-flight"
                    columns={columns}
                    data={inFlightPayments}
                    rowKey="uuid"
                    formatOptions={formatOptions}
                    emptyMessage=""
                  />
                </div>
              </>
            )}

            {/* Only a rule BETWEEN two groups that both have rows. With the
              empty groups gone it would otherwise hang under nothing. */}
            {processedPayments.length > 0 &&
              (pendingPayments.length > 0 || inFlightPayments.length > 0) && (
                <Separator className="opacity-60" />
              )}

            {processedPayments.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  {t('settlement.payments.groupProcessed', 'Processed')}
                </span>
                <DataTable
                  tableId="settlement-payments-processed"
                  columns={columns}
                  data={processedPayments}
                  rowKey="uuid"
                  formatOptions={formatOptions}
                  emptyMessage=""
                />
              </div>
            )}

            {voidedPayments.length > 0 && (
              <>
                <Separator className="opacity-60" />
                <div className="flex flex-col gap-2">
                  <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                    {t('settlement.payments.groupVoided', 'Voided')}
                  </span>
                  <DataTable
                    tableId="settlement-payments-voided"
                    columns={columns}
                    data={voidedPayments}
                    rowKey="uuid"
                    formatOptions={formatOptions}
                    emptyMessage=""
                  />
                </div>
              </>
            )}

            {cancelledPayments.length > 0 && (
              <>
                <Separator className="opacity-60" />
                <div className="flex flex-col gap-2">
                  <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                    {t('settlement.payments.groupCancelled', 'Cancelled')}
                  </span>
                  <DataTable
                    tableId="settlement-payments-cancelled"
                    columns={columns}
                    data={cancelledPayments}
                    rowKey="uuid"
                    formatOptions={formatOptions}
                    emptyMessage=""
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
      <ConfirmationDialog
        open={!!submitTarget}
        onOpenChange={(next) => {
          if (!next) setSubmitTarget(null)
        }}
        title={t('claims.payments.sendPaymentTitle', 'Send this payment?')}
        description={
          submitTarget
            ? t(
                'claims.payments.sendPaymentBody',
                '{{amount}} to {{payee}}. {{provider}} pays this out straight away.',
                {
                  amount: formatCurrency(submitTarget.amount, currency, locale),
                  payee:
                    submitTarget.payee ||
                    t('claims.payments.unnamedPayee', 'the recipient'),
                  provider: submitTarget.disbursement_provider,
                },
              )
            : undefined
        }
        confirmLabel={t('claims.payments.sendPaymentConfirm', 'Send payment')}
        pendingLabel={t('claims.payments.sendPaymentPending', 'Sending...')}
        isPending={submitMut.isPending}
        onConfirm={async () => {
          if (!submitTarget) return
          try {
            await submitMut.mutateAsync({ paymentUuid: submitTarget.uuid })
            setSubmitTarget(null)
          } catch (err) {
            toastApiError(
              err,
              t(
                'claims.payments.sendPaymentFailed',
                'Could not send the payment',
              ),
            )
          }
        }}
      />
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {t('settlement.payments.addPaymentTitle', 'Add Payment')}
            </DialogTitle>
          </DialogHeader>
          <ResourceLookupContextProvider providers={lookupProviders}>
            <Form className="grid grid-cols-2 gap-4 py-2" />
          </ResourceLookupContextProvider>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDialogClose}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button onClick={() => ref.current?.submitForm()} disabled={busy}>
              {busy ? 'Saving…' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Payment - captures the reference number and optional
          note for the individual payment row. Each payment carries its
          own reference (check #, ACH confirmation, credit #, …) so the
          SAP ledger can match the right transaction. */}
      {/* WPS-175 #15: batch dialog. Names every payment it is about to
        process, for the same reason the single one does - the operator
        should be able to see what they are signing off, not trust a count. */}
      <Dialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('claims.payments.processAllTitle', 'Process All Payments')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'claims.payments.processAllHelp',
                'These payments will be processed with the same reference and post date. To give each its own reference, close this and use Process Payment on the row.',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-3">
              {processablePayments.map((payment) => (
                <div
                  key={payment.uuid}
                  className="flex items-center justify-between py-1 text-[12px]"
                >
                  <span className="text-[var(--ink-2)]">
                    {payment.payee || '-'}
                  </span>
                  <span className="font-medium text-[var(--ink)]">
                    {formatCurrency(payment.amount, currency, locale)}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-1">
              <Label className="mb-1.5 block clara-label font-normal">
                {t('claims.payments.referenceLabel', 'Payment reference')}
              </Label>
              <Input
                value={bulkReference}
                onChange={(e) => {
                  setBulkReference(e.target.value)
                  if (bulkError) setBulkError(null)
                }}
                placeholder={t(
                  'claims.payments.referencePlaceholder',
                  'Applied to every payment above',
                )}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="mb-1.5 block clara-label font-normal">
                {t('claims.payments.postDateLabel', 'Post date')}{' '}
                {postDateRequired ? (
                  <span className="text-[var(--status-error)]">*</span>
                ) : (
                  <span className="text-[var(--ink-3)]">
                    ({t('common.optional', 'optional')})
                  </span>
                )}
              </Label>
              <DatePicker
                value={bulkPostDate}
                onChange={setBulkPostDate}
                placeholder="MM/DD/YYYY"
              />
            </div>
            {bulkError && (
              <p className="text-[12px] text-[var(--status-error)]">
                {bulkError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkOpen(false)}
              disabled={bulkBusy}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleBulkProcess} disabled={bulkBusy}>
              {bulkBusy
                ? t('claims.payments.processing', 'Processing...')
                : `${t('claims.payments.processAll', 'Process All')} (${processablePayments.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WPS-165 #13/#14: one field, for a number that turned up after the
          payment did. Deliberately not the Process dialog - that action has
          already happened and re-opening it to type a cheque number would
          ask the operator to re-confirm things that are already settled. */}
      <Dialog
        open={referenceTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeReferenceDialog()
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>
              {t('settlement.payments.addReferenceTitle', 'Add Reference')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'settlement.payments.addReferenceHelp',
                'Record the check, ACH or credit number this payment settled under. Once saved it cannot be edited here.',
              )}
            </DialogDescription>
          </DialogHeader>
          {/* WPS-165 #13: the post date is captured with the reference; it is
              required when the tenant's payment_date attribute is. */}
          <ReferenceForm className="grid grid-cols-1 gap-4 py-2" />
          {referenceDuplicate && (
            <div className="flex items-start gap-2.5 rounded-[6px] border-[0.5px] border-[var(--status-warn)] bg-[var(--bg-light)] px-3.5 py-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--status-warn)]" />
              <p className="text-[12px] text-[var(--ink-2)]">
                {referenceDuplicate.message}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeReferenceDialog}
              disabled={referenceMut.isPending}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={() => referenceFormRef.current?.submitForm()}
              disabled={referenceMut.isPending}
            >
              {referenceDuplicate
                ? t('settlement.payments.saveReferenceAnyway', 'Save Anyway')
                : t('settlement.payments.saveReference', 'Save Reference')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={processTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeProcessDialog()
        }}
      >
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>
              {t('settlement.payments.processPaymentTitle', 'Process Payment')}
            </DialogTitle>
            <DialogDescription>
              Marks this payment as processed and locks the line items it
              covers. Other pending payments on this claim aren't affected.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            {/* WPS-175 #15: identify the payment by WHAT IT PAYS FOR, first.
              Recipient and payee are optional EAV and are unset on a payment
              materialised from line items, so this block used to read
              "- - - / $484" and identify nothing. The line items are always
              there: "Dump Charge - $484" is how someone recognises it. Payee
              and recipient still show when they exist, on their own row, and
              are simply omitted when they do not rather than rendered as
              dashes. */}
            {processTarget && (
              <div className="flex flex-col gap-2 rounded-[6px] bg-[var(--bg-subtle)] px-3 py-2.5 text-[12px] text-[var(--ink-2)]">
                {(processTarget.covers ?? []).length > 0 ? (
                  <div className="flex flex-col gap-1">
                    {(processTarget.covers ?? []).map((item, idx) => (
                      <div
                        key={`${item.label}-${idx}`}
                        className="flex items-baseline justify-between gap-2"
                      >
                        <span className="text-[var(--ink-1)]">
                          {item.label ||
                            t('claims.payments.unnamedItem', 'Line item')}
                        </span>
                        <span className="tabular-nums">
                          {formatCurrency(
                            Number(item.amount),
                            currency,
                            locale,
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="flex items-baseline justify-between gap-2 border-t-[0.5px] border-t-[var(--rule)] pt-2">
                  <span className="font-medium text-[var(--ink-1)]">
                    {t('claims.payments.paymentTotal', 'Payment total')}
                  </span>
                  <span className="font-medium tabular-nums text-[var(--ink-1)]">
                    {formatCurrency(processTarget.amount, currency, locale)}
                  </span>
                </div>
                <div className="text-[11px] text-[var(--ink-3)]">
                  {[
                    paymentTypeOptions.find(
                      (o) => o.value === processTarget.payment_type,
                    )?.label ?? processTarget.payment_type,
                    processTarget.recipient_type
                      ? (recipientTypeOptions.find(
                          (o) => o.value === processTarget.recipient_type,
                        )?.label ?? processTarget.recipient_type)
                      : null,
                    processTarget.payee || null,
                    processTarget.payment_date || null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
            )}
            {/* WPS-175 #11: a reference that arrived from SAP or the
                automated check download is authoritative - it is shown but
                cannot be hand-edited. Manual entry is offered only while
                the field is genuinely blank. */}
            <div>
              <label className="mb-1.5 block clara-label font-normal">
                Reference Number{' '}
                {!referenceLocked &&
                  (referenceRequired ? (
                    <span className="text-[var(--status-error)]">*</span>
                  ) : (
                    <span className="text-[var(--ink-3)] font-normal normal-case tracking-normal">
                      {t('settlement.payments.referenceOptional', 'optional')}
                    </span>
                  ))}
              </label>
              <Input
                value={
                  referenceLocked
                    ? (processTarget?.settlement_reference ?? '')
                    : processReference
                }
                onChange={(e) => {
                  setProcessReference(e.target.value)
                  if (processReferenceError) setProcessReferenceError(false)
                  // A different number is a different question - retract the
                  // confirmation so it has to be answered again.
                  if (duplicateWarning) setDuplicateWarning(null)
                }}
                placeholder={t(
                  'settlement.payments.referencePlaceholder',
                  'Check #, ACH confirmation, credit # …',
                )}
                disabled={processMut.isPending || referenceLocked}
                readOnly={referenceLocked}
                aria-invalid={processReferenceError}
              />
              {referenceLocked ? (
                <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                  {/* WPS-165 #14: the API knows where the number came from,
                      so say that rather than assuming every locked reference
                      arrived from SAP - one typed by a colleague yesterday is
                      equally locked, for a different reason. */}
                  {processTarget?.reference_origin ||
                    t(
                      'settlement.payments.referenceLockedHelp',
                      'Synchronised from the payment system, so it cannot be edited here.',
                    )}
                </p>
              ) : processReferenceError ? (
                <p className="mt-1 text-[11px] text-[var(--status-error)]">
                  {t(
                    'settlement.payments.referenceRequiredError',
                    'Reference number is required.',
                  )}
                </p>
              ) : (
                !referenceRequired && (
                  <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                    {t(
                      'settlement.payments.referenceLaterHelp',
                      'Leave blank if the number has not come back yet. You can add it from the Reference column once it does.',
                    )}
                  </p>
                )
              )}
            </div>
            {/* WPS-175 #10: the date the payment posts, which can differ
                from the day it is keyed in (a check written today may post
                tomorrow). Optional - left blank, the post date falls back
                to the processing date. */}
            <div>
              <label className="mb-1.5 block clara-label font-normal">
                {t('settlement.payments.postDateLabel', 'Post Date')}{' '}
                {/* WPS-165 #13: required only when a reference is being
                    entered here and the tenant requires the post date.
                    Record the payment with neither and both arrive later. */}
                {postDateRequired &&
                (processReference.trim() || referenceLocked) ? (
                  <span className="text-[var(--status-error)]">*</span>
                ) : (
                  <span className="text-[var(--ink-3)]">
                    {t('common.optional', '(optional)')}
                  </span>
                )}
              </label>
              {/* Not <Input type="date">: the native control renders in the
                  BROWSER's locale, which the app cannot set, so the picker
                  showed 01/09/2026 while the table beside it showed
                  09/01/2026 for the same day. DatePicker derives its format
                  from the org locale (MM/dd/yyyy for OC's en-US), so entry
                  and display finally agree. */}
              <DatePicker
                value={processPostDate}
                onChange={(v) => {
                  setProcessPostDate(v)
                  if (processPostDateError) setProcessPostDateError(false)
                }}
              />
              {processPostDateError && (
                <p className="mt-1 text-[11px] text-[var(--status-error)]">
                  {t(
                    'settlement.payments.postDateRequiredWithReference',
                    'Enter the post date for this reference number.',
                  )}
                </p>
              )}
            </div>
            <div>
              <label className="mb-1.5 block clara-label font-normal">
                {t('settlement.payments.noteLabel', 'Note')}{' '}
                <span className="text-[var(--ink-3)]">
                  {t('common.optional', '(optional)')}
                </span>
              </label>
              <Textarea
                value={processNote}
                onChange={(e) => setProcessNote(e.target.value)}
                placeholder={t(
                  'settlement.payments.notePlaceholder',
                  'Bank batch ID, AP note, etc.',
                )}
                rows={2}
                disabled={processMut.isPending}
                className="min-h-0 resize-none text-sm"
              />
            </div>
          </div>
          {duplicateWarning && (
            <div className="flex items-start gap-2.5 rounded-[6px] border-[0.5px] border-[var(--status-warn)] bg-[var(--bg-light)] px-3.5 py-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--status-warn)]" />
              <div className="flex flex-col gap-1">
                <span className="text-[13px] font-medium text-[var(--ink)]">
                  Possible duplicate payment
                </span>
                <span className="text-[13px] font-light leading-[1.5] text-[var(--ink-2)]">
                  {duplicateWarning}
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeProcessDialog}
              disabled={processMut.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleProcess} disabled={processMut.isPending}>
              {processMut.isPending
                ? t('claims.payments.processing', 'Processing...')
                : duplicateWarning
                  ? t('claims.payments.processAnyway', 'Process anyway')
                  : t('claims.payments.processPayment', 'Process Payment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {voidTarget && (
        <VoidPaymentDialog
          claimUuid={claimUuid}
          payment={voidTarget}
          amount={formatCurrency(voidTarget.amount, currency, locale)}
          onClose={() => setVoidTarget(null)}
        />
      )}

      {/* Cancel Payment Confirmation */}
      <Dialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCancelTarget(null)
            setCancelReason('')
            setCancelReasonError('')
          }
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>
              {t('settlement.payments.cancelPaymentTitle', 'Cancel Payment')}
            </DialogTitle>
            <DialogDescription>
              {cancelTarget?.settlement_reference ? (
                <>
                  This will cancel payment{' '}
                  <span className="font-medium">
                    #{cancelTarget.settlement_reference}
                  </span>{' '}
                  for{' '}
                </>
              ) : (
                'This will cancel the payment for '
              )}
              <span className="font-medium text-[var(--status-error)]">
                {cancelTarget
                  ? formatCurrency(cancelTarget.amount, currency, locale)
                  : ''}
              </span>
              . The settlement lines it covered stay with this payment and
              won&apos;t be paid. To pay them differently, change the lines
              instead of cancelling.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Label className="mb-1.5 block" htmlFor="cancel-payment-reason">
              {t('settlement.payments.cancelReasonLabel', 'Reason')}{' '}
              <span className="text-[var(--status-error)]">*</span>
            </Label>
            <Textarea
              id="cancel-payment-reason"
              rows={3}
              className={
                cancelReasonError
                  ? 'ring-1 ring-[var(--status-error)]'
                  : undefined
              }
              placeholder={t(
                'settlement.payments.cancelReasonPlaceholder',
                'Why is this payment not going ahead?',
              )}
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value)
                if (cancelReasonError) setCancelReasonError('')
              }}
            />
            {cancelReasonError && (
              <p className="mt-1 text-xs text-[var(--status-error)]">
                {cancelReasonError}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCancelTarget(null)
                setCancelReason('')
                setCancelReasonError('')
              }}
              disabled={cancelMut.isPending}
            >
              {t('common.close', 'Close')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelPayment}
              disabled={cancelMut.isPending}
            >
              {cancelMut.isPending
                ? t('settlement.payments.cancelling', 'Cancelling')
                : t('settlement.payments.cancelConfirm', 'Cancel Payment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DetailCard>
  )
}

/** WPS-243: void a paid payment, with the reason on the record. */
function VoidPaymentDialog({
  claimUuid,
  payment,
  amount,
  onClose,
}: {
  claimUuid: string
  payment: ClaimPayment
  amount: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const voidMut = useVoidPayment(claimUuid)
  const fields: FormField[] = [
    {
      type: 'textarea',
      name: 'reason',
      label: t('settlement.payments.voidReason', 'Reason'),
      value: '',
      required: t(
        'settlement.payments.voidReasonRequired',
        'Say why the payment is being voided.',
      ),
    },
  ]
  const { Form } = useForm(
    { name: 'void-payment-form', submit: 'footer', fields },
    async (values) => {
      try {
        await voidMut.mutateAsync({
          paymentUuid: payment.uuid,
          reason: String(values.reason ?? '').trim(),
        })
        toast.success(t('settlement.payments.voided', 'Payment voided.'))
        onClose()
      } catch (err) {
        toastApiError(
          err,
          t('settlement.payments.voidFailed', 'The payment could not be voided.'),
        )
      }
    },
    [payment.uuid, voidMut, onClose, t],
  )
  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>
            {t('settlement.payments.voidTitle', 'Void this payment?')}
          </DialogTitle>
          <DialogDescription>
            {t('settlement.payments.voidBody', {
              defaultValue:
                'This {{amount}} payment ({{reference}}) was paid. Voiding keeps it in the payment history as Voided and takes what it paid out of the settlement. Enter what is still owed and it is paid as a replacement.',
              amount,
              reference: payment.settlement_reference || payment.reference_number,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Form className="flex flex-col gap-4" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={voidMut.isPending}>
            {t('common.close', 'Close')}
          </Button>
          <Button
            type="submit"
            form="void-payment-form"
            variant="destructive"
            disabled={voidMut.isPending}
          >
            {t('settlement.payments.voidConfirm', 'Void payment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
